import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button, Chip, EmptyState, MasterDetail, type MasterDetailItem } from '@/components/ui'
import { useFormulations } from '../api/queries'
import type { FormulationSummary } from '../api/types'
import { FormulationDetail } from '../components/FormulationDetail'
import { ImportTemplateButton } from '../components/ImportTemplateButton'
import { StatusChip } from '../components/StatusChip'

/**
 * Formulations — Active Formulas (Sprint 08 narrow v1).
 *
 * MasterDetail: list of formulations on the left (searchable by
 * formula_code / name), editor on the right. Header actions: New
 * (blank draft) + Import template (.xlsx upload).
 */

interface ListRow extends MasterDetailItem {
  formulation: FormulationSummary
}

const NEW_SENTINEL = '__new__'

export function FormulationsPage() {
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  // Sprint 08b — toggle to surface soft-deleted formulations in the list.
  const [showArchived, setShowArchived] = useState(false)

  const { data, isLoading, isError } = useFormulations({
    q: query,
    includeArchived: showArchived,
  })
  const formulations = useMemo<FormulationSummary[]>(
    () => data?.formulations ?? [],
    [data],
  )

  const isCreating = selectedId === NEW_SENTINEL

  const items = useMemo<ListRow[]>(
    () =>
      formulations.map((f) => ({
        id: String(f.id),
        primary: f.name,
        secondary: f.formula_code,
        meta: (
          <span className="flex flex-wrap items-center gap-1">
            <StatusChip status={f.status} />
            {f.deleted_at ? (
              <Chip size="xs" variant="neutral">
                Archived
              </Chip>
            ) : null}
            <span className="opacity-40">·</span>
            <span>batch {Number(f.batch_size_g).toLocaleString()}g</span>
            {f.updated_at ? (
              <>
                <span className="opacity-40">·</span>
                <span className="text-tertiary">
                  {new Date(f.updated_at).toLocaleDateString()}
                </span>
              </>
            ) : null}
          </span>
        ),
        formulation: f,
      })),
    [formulations],
  )

  const handleImported = (newId: number) => setSelectedId(String(newId))
  const handleNew = () => setSelectedId(NEW_SENTINEL)
  const handleCreated = (newId: number) => setSelectedId(String(newId))
  const handleCancelCreate = () => setSelectedId(undefined)

  const showEmptyState =
    !isLoading && !isCreating && formulations.length === 0 && query.trim() === ''

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-start justify-between gap-4 border-b border-border-subtle bg-surface px-6 py-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-role-page-title leading-tight tracking-tight">
            Formulations
          </h1>
          <p className="text-sm text-secondary">
            Active formulas — ingredient rows, phase grouping, % totals, batch scaling.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Sprint 08b — show archived toggle. */}
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-secondary">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="cursor-pointer"
            />
            Show archived
          </label>
          <Button
            variant="primary"
            size="sm"
            onClick={handleNew}
            disabled={isCreating}
            title="Start a new formulation from scratch"
          >
            <Plus size={14} />
            New
          </Button>
          <ImportTemplateButton onImported={handleImported} />
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-hidden p-4">
        {isError ? (
          <EmptyState
            variant="error"
            title="Couldn't load formulations"
            description="The /api/formulations endpoint returned an error. Check that the middleware container is up to date."
          />
        ) : isCreating ? (
          // Create mode bypasses MasterDetail, so it needs its own
          // scroll region. (When MasterDetail wraps a row, its slot
          // already supplies overflow-y-auto + a height bound.)
          <div className="h-full overflow-y-auto rounded-md border border-border-subtle bg-surface">
            <FormulationDetail
              formulationId={null}
              onCreated={handleCreated}
              onCancelCreate={handleCancelCreate}
            />
          </div>
        ) : showEmptyState ? (
          <EmptyState
            title="No formulations yet"
            description="Create a blank formulation with New, or import your first from an Excel template using the button above."
          />
        ) : (
          <MasterDetail<ListRow>
            items={items}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onSearch={setQuery}
            searchPlaceholder={
              isLoading
                ? 'Loading formulations…'
                : `Search ${formulations.length} formulations`
            }
            autoSelectFirst
            renderDetail={(row) => (
              <FormulationDetail
                formulationId={row.formulation.id}
                onDeleted={() => setSelectedId(undefined)}
              />
            )}
          />
        )}
      </div>
    </div>
  )
}
